import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  isEqual,
  includes,
  keyBy,
  filter,
  reject,
  set,
  get,
  has,
  sumBy,
  size,
  values,
  find,
  getOr,
  isEmpty
} from 'lodash/fp';
import {
  Schema,
  Form,
  FormGroup,
  FormControl,
  Button,
  Icon,
  Alert,
  DatePicker
} from 'rsuite';
import { PackageType } from '../../../constants/inventory/InventoryTypes';
import InventoryActions from '../../../constants/inventory/InventoryActions';
import {
  InventoryItemType,
  InventoryPackageType,
  InventoryItemSkuType,
  InventoryAvailabilityType
} from '../../../interfaces/inventory';
import { ControlLabel, SelectPicker } from '../../widgets/FormElements';
import InventoryImage from '../Image';
import InventoryItemAvailability from '../ItemAvailability';
import { ReasonsByActionOptions } from './reason-options';
import { parseInt } from '../../../helpers/utils';

const {
  ADD,
  MOVE,
  REMOVE,
  ORDER_RECEIVED,
  DONATION_RECEIVED,
  DONATION_REMOVED
} = InventoryActions;
const { NumberType, StringType, DateType } = Schema.Types;

const defualtFormValue = {
  action: '',
  zoneId: null,
  quantity: '',
  note: '',
  performedOn: new Date()
};

const QtyPrefixByAction = {
  [ADD]: '+',
  [REMOVE]: '-'
};

export default class InventoryAdjustItemForm extends Component {
  static propTypes = {
    action: PropTypes.oneOf(values(InventoryActions)).isRequired,
    zoneId: PropTypes.number,
    item: PropTypes.oneOfType([InventoryItemType, InventoryPackageType]),
    skus: PropTypes.arrayOf(InventoryItemSkuType),
    availability: PropTypes.arrayOf(InventoryAvailabilityType),
    zoneOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    donorOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isSaving: PropTypes.bool.isRequired,
    isSaved: PropTypes.bool.isRequired,
    isFetchingZones: PropTypes.bool.isRequired,
    isFetchingDonors: PropTypes.bool.isRequired,
    onInit: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired
  };

  static defaultProps = {
    zoneId: null,
    item: null,
    skus: [],
    availability: []
  };

  constructor(props) {
    super(props);
    const { action, zoneId } = props;
    this.state = {
      formValue: { ...defualtFormValue, action, zoneId },
      formError: {}
    };
    this.formRef = React.createRef();
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    const { onInit } = this.props;
    onInit();
  }

  componentDidUpdate({ isSaved: prevIsSaved }) {
    const { isSaved, onSaved } = this.props;
    if (!prevIsSaved && isSaved) {
      onSaved();
    }
  }

  get isPackage() {
    const { item } = this.props;
    return isEqual(PackageType, get('itemType', item));
  }

  get zoneOptions() {
    const { availability, zoneOptions, action } = this.props;
    const actionsWithAllLocations = [ADD, ORDER_RECEIVED, DONATION_RECEIVED];
    const showAllLocations = includes(action, actionsWithAllLocations);
    if (!showAllLocations) {
      const existingLocations = keyBy(
        ({ zone }) => zone && zone.id,
        availability
      );
      return filter(({ value }) => has(value, existingLocations), zoneOptions);
    }
    return zoneOptions;
  }

  get destinationZoneOptions() {
    const { formValue } = this.state;
    const { zoneOptions } = this.props;
    return reject({ value: formValue.zoneId }, zoneOptions);
  }

  get model() {
    const { action } = this.props;
    const destinationZoneId = NumberType().isRequired('This field is required');
    const model = {
      action: NumberType().isRequired('This field is required'),
      zoneId: NumberType().isRequired('This field is required'),
      quantity: NumberType()
        .addRule((value, { zoneId }) => {
          if (!value || !zoneId) return true;
          return this.validateQuantity(parseInt(value), zoneId);
        }, 'Must be equal or less than available quantity')
        .addRule((value, { zoneId }) => {
          if (!value || !zoneId) return true;
          return this.validateForOnholdQuantity(parseInt(value), zoneId);
        }, 'Must have enough remaining for onhold quantity')
        .isRequired('This field is required'),
      note: StringType(),
      ...(action === MOVE ? { destinationZoneId } : {}),
      performedOn: DateType().max(
        new Date(),
        'Must not later than current date'
      )
    };
    return Schema.Model(model);
  }

  checkItemAvailabilityForZone = zoneId => {
    const { action, item, availability } = this.props;
    const itemAvailability = find(
      ({ item: { id }, zone }) => item.id === id && zone.id === zoneId,
      availability
    );
    const isMoveOrDeductAction = includes(action, [MOVE, REMOVE]);
    if (
      itemAvailability &&
      isMoveOrDeductAction &&
      itemAvailability.quantity < 1
    ) {
      Alert.error(`Unable to select. Available quantity is 0`, 5000);
      return false;
    }
    return true;
  };

  handleFormChange = formValue => {
    if (this.checkItemAvailabilityForZone(formValue.zoneId)) {
      this.setState({ formValue });
    }
  };

  handleSelectItemAvailability = ({ zone }) => {
    const { formValue } = this.state;
    if (this.checkItemAvailabilityForZone(zone.id)) {
      this.setState({ formValue: set('zoneId', zone.id, formValue) });
    }
  };

  handleSubmit = () => {
    if (!this.formRef.current.check()) return;

    const { onSubmit } = this.props;
    const { formValue } = this.state;
    onSubmit(formValue);
  };

  validateQuantity(quantity, zoneId) {
    const { action, item, availability } = this.props;
    if (action !== MOVE && action !== REMOVE) return true;
    const itemAvailability = find(
      ({ item: { id }, zone }) => item.id === id && zone.id === zoneId,
      availability
    );
    return itemAvailability.quantity >= quantity;
  }

  validateForOnholdQuantity(quantity, zoneId) {
    const { action, item, availability } = this.props;
    if (action !== MOVE && action !== REMOVE) return true;
    const itemAvailability = find(
      ({ item: { id }, zone }) => item.id === id && zone.id === zoneId,
      availability
    );
    const { onholdQuantity } = itemAvailability;
    return itemAvailability.quantity - quantity >= onholdQuantity;
  }

  render() {
    const {
      action,
      isSaving,
      donorOptions,
      isFetchingZones,
      isFetchingDonors,
      item,
      availability
    } = this.props;
    const { formValue, formError } = this.state;

    const { action: reason } = formValue;
    const isDonation =
      reason === DONATION_RECEIVED || reason === DONATION_REMOVED;

    return (
      <Form
        style={{ maxWidth: 500 }}
        layout="horizontal"
        model={this.model}
        ref={this.formRef}
        formValue={formValue}
        onChange={this.handleFormChange}
        onCheck={error => {
          this.setState({ formError: error });
        }}
      >
        <div
          style={{
            verticalAlign: 'top',
            paddingBottom: 10,
            marginBottom: 10,
            marginRight: 20,
            borderBottom: '1px solid #ddd'
          }}
        >
          <InventoryImage
            image={item.photo}
            alt="photo"
            style={{ width: 50, float: 'left', marginRight: 20 }}
          />
          <div
            style={{
              display: 'inline-block',
              width: '60%',
              verticalAlign: 'top'
            }}
          >
            <h5 style={{ marginBottom: 10, marginTop: 0 }}>{item.name}</h5>
          </div>
          <div
            style={{
              display: 'inline-block',
              width: '25%',
              textAlign: 'right'
            }}
          >
            <h5 style={{ marginBottom: 5, marginTop: 0 }}>
              ${parseFloat(item.price).toFixed(2)}
            </h5>
            <span style={{ color: '#8e8e93', marginRight: 10 }}>
              Qty Available:
            </span>
            <span style={{ fontSize: '14px' }}>
              {sumBy('quantity', availability)}
            </span>
          </div>
          <div style={{ marginTop: 10 }}>
            <span style={{ fontSize: '14px' }}>{item.description}</span>
          </div>
        </div>
        {size(availability) > 0 && (
          <FormGroup>
            <ControlLabel>Select item location</ControlLabel>
            <InventoryItemAvailability
              selected={formValue.zoneId}
              availability={availability}
              onSelectItem={this.handleSelectItemAvailability}
            />
          </FormGroup>
        )}
        <FormGroup>
          <ControlLabel>Location</ControlLabel>
          <FormControl
            name="zoneId"
            placeholder={isFetchingZones ? 'Loading...' : 'Select Location'}
            groupBy="location"
            accepter={SelectPicker}
            data={this.zoneOptions}
            errorMessage={formError.zoneId}
            renderValue={(_, each) => (
              <div>
                {get('location', each)}, {get('label', each)}
              </div>
            )}
          />
        </FormGroup>
        {action === MOVE && (
          <FormGroup>
            <ControlLabel>Move to</ControlLabel>
            <FormControl
              name="destinationZoneId"
              placeholder={
                isFetchingZones ? 'Loading...' : 'Select Destination Location'
              }
              errorMessage={formError.destinationZoneId}
              groupBy="location"
              accepter={SelectPicker}
              data={this.destinationZoneOptions}
              renderValue={(_, each) => (
                <div>
                  {get('location', each)}, {get('label', each)}
                </div>
              )}
            />
          </FormGroup>
        )}
        {has(action, ReasonsByActionOptions) && (
          <FormGroup>
            <ControlLabel>Reason</ControlLabel>
            <FormControl
              name="action"
              placeholder="Select reason"
              errorMessage={formError.action}
              accepter={SelectPicker}
              data={get(action, ReasonsByActionOptions)}
            />
          </FormGroup>
        )}
        {isDonation && (
          <FormGroup>
            <ControlLabel>Donor</ControlLabel>
            <FormControl
              name="donorId"
              placeholder={isFetchingDonors ? 'Loading...' : 'Select Donor'}
              accepter={SelectPicker}
              data={donorOptions}
              errorMessage={formError.donorId}
            />
          </FormGroup>
        )}
        <FormGroup>
          <ControlLabel>
            {getOr('', action, QtyPrefixByAction)} Quantity
          </ControlLabel>
          <FormControl name="quantity" defaultValue={0} />
        </FormGroup>
        <FormGroup>
          <ControlLabel>Note</ControlLabel>
          <FormControl rows={5} name="note" componentClass="textarea" />
        </FormGroup>
        <FormGroup>
          <ControlLabel>Date Performed</ControlLabel>
          <FormControl
            name="performedOn"
            accepter={props => <DatePicker oneTap {...props} />}
          />
        </FormGroup>
        <FormGroup style={{ textAlign: 'right', marginRight: 18 }}>
          <Button
            onClick={this.handleSubmit}
            appearance="primary"
            disabled={isSaving || !isEmpty(formError)}
          >
            <Icon icon="save" /> {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </FormGroup>
      </Form>
    );
  }
}
